/**
 * Prompt Optimizer — Build the final cinematic prompt from a SceneGraph.
 * Layer 3: Final Cinematic Prompt
 * Only includes non-null fields. Professional cinematographer language.
 */

import type { SceneGraph } from './sceneGraph';

// ─── Vocabulary maps for professional language ──

const SHOT_TYPE_VOCAB: Record<string, string> = {
  close_up: 'intimate close-up',
  medium: 'medium shot',
  wide: 'cinematic wide shot',
  extreme_wide: 'sweeping extreme wide shot',
};

const ANGLE_VOCAB: Record<string, string> = {
  eye_level: 'eye-level perspective',
  low_angle: 'dramatic low-angle perspective',
  high_angle: 'commanding high-angle view',
  birds_eye: "bird's-eye overhead view",
  dutch_angle: 'unsettling Dutch angle',
};

const LIGHTING_VOCAB: Record<string, string> = {
  dramatic: 'dramatic cinematic lighting',
  soft: 'soft diffused lighting',
  neon: 'vibrant neon-lit illumination',
  candlelight: 'warm candlelight glow',
  moonlight: 'ethereal moonlight',
  sunlight: 'natural sunlight',
  backlight: 'striking backlight silhouette',
  spotlight: 'focused spotlight',
  volumetric: 'volumetric god-ray lighting',
  natural: 'natural ambient lighting',
};

const INTENSITY_VOCAB: Record<string, string> = {
  low_key: 'low-key chiaroscuro',
  high_key: 'high-key luminous',
  balanced: 'balanced three-point',
};

const MOOD_VOCAB: Record<string, string> = {
  eerie: 'eerie unsettling atmosphere',
  dramatic: 'dramatic cinematic tension',
  peaceful: 'serene peaceful ambiance',
  tense: 'high-tension suspense',
  dark: 'ominous dark atmosphere',
  mystical: 'mystical ethereal aura',
  heroic: 'grand heroic grandeur',
  melancholic: 'melancholic introspective tone',
  warm: 'warm inviting warmth',
  cold: 'stark cold isolation',
};

/**
 * Build the final professional cinematic prompt from a fully-resolved SceneGraph.
 */
export function buildFinalPrompt(graph: SceneGraph): string {
  const parts: string[] = [];

  // ── Shot description ──
  const shotParts: string[] = [];
  if (graph.camera.shotType) {
    shotParts.push(SHOT_TYPE_VOCAB[graph.camera.shotType] || graph.camera.shotType);
  }

  // ── Subject ──
  if (graph.character.type) {
    const charParts: string[] = [];

    // Emotion + action
    if (graph.character.emotion && graph.character.emotion !== 'neutral') {
      charParts.push(graph.character.emotion);
    }
    charParts.push(graph.character.type);

    if (graph.character.clothing) {
      charParts.push(`wearing ${graph.character.clothing}`);
    }
    if (graph.character.pose && graph.character.pose !== 'standing') {
      charParts.push(graph.character.pose);
    }

    const subjectStr = charParts.join(' ');
    if (shotParts.length) {
      parts.push(`${shotParts.join(' ')} of a ${subjectStr}`);
    } else {
      parts.push(`a ${subjectStr}`);
    }
  } else if (shotParts.length) {
    parts.push(shotParts.join(' '));
  }

  // ── Skin / detail ──
  if (graph.character.skinDetail) {
    parts.push(graph.character.skinDetail);
  }

  // ── Environment ──
  if (graph.environment.location) {
    parts.push(`in ${graph.environment.location}`);
  }

  // ── Atmosphere ──
  if (graph.environment.atmosphere) {
    parts.push(graph.environment.atmosphere);
  }

  // ── Weather ──
  if (graph.environment.weather) {
    parts.push(`${graph.environment.weather} conditions`);
  }

  // ── Camera details ──
  const cameraParts: string[] = [];
  if (graph.camera.lens) cameraParts.push(`${graph.camera.lens} lens`);
  if (graph.camera.angle) cameraParts.push(ANGLE_VOCAB[graph.camera.angle] || graph.camera.angle);
  if (graph.camera.dof === 'shallow') cameraParts.push('shallow depth of field with creamy bokeh');
  else if (graph.camera.dof === 'deep') cameraParts.push('deep focus');
  if (graph.camera.movement && graph.camera.movement !== 'static') cameraParts.push(`${graph.camera.movement} camera movement`);
  if (cameraParts.length) parts.push(cameraParts.join(', '));

  // ── Lighting ──
  const lightParts: string[] = [];
  if (graph.lighting.type) lightParts.push(LIGHTING_VOCAB[graph.lighting.type] || graph.lighting.type);
  if (graph.lighting.intensity) lightParts.push(INTENSITY_VOCAB[graph.lighting.intensity] || graph.lighting.intensity);
  if (graph.lighting.direction && graph.lighting.direction !== 'front') lightParts.push(`${graph.lighting.direction} light`);
  if (graph.lighting.colorTemp === 'warm') lightParts.push('warm color temperature');
  else if (graph.lighting.colorTemp === 'cool') lightParts.push('cool blue tones');
  if (lightParts.length) parts.push(lightParts.join(', '));

  // ── Mood ──
  if (graph.style.mood) {
    parts.push(MOOD_VOCAB[graph.style.mood] || graph.style.mood);
  }

  // ── Style ──
  if (graph.style.renderStyle) {
    parts.push(`${graph.style.renderStyle} render`);
  }

  // ── Color palette ──
  if (graph.style.colorPalette) {
    parts.push(`${graph.style.colorPalette} color palette`);
  }

  // ── Time ──
  if (graph.environment.time) {
    const timeStr = graph.environment.time.replace(/_/g, ' ');
    parts.push(`${timeStr} setting`);
  }

  // ── Quality tags ──
  parts.push('highly detailed, 8K resolution');

  // remove duplicate fragments to avoid repetition (user may have entered
  // the same adjective twice, etc.)
  const uniqueParts: string[] = [];
  for (const p of parts) {
    if (!uniqueParts.includes(p)) uniqueParts.push(p);
  }

  return uniqueParts.join(', ');
}

/**
 * Build a debug view showing what's user-specified vs inferred vs NULL
 */
export function buildAnalysisView(graph: SceneGraph): {
  userSpecified: Record<string, string>;
  inferred: Record<string, string>;
  nullFields: string[];
} {
  const userSpecified: Record<string, string> = {};
  const inferred: Record<string, string> = {};
  const nullFields: string[] = [];

  const checkNode = (node: Record<string, unknown>, prefix: string) => {
    for (const [key, value] of Object.entries(node)) {
      const path = `${prefix}.${key}`;
      if (value === null) {
        nullFields.push(path);
      } else if (graph.meta.userSpecifiedFields.includes(path)) {
        userSpecified[path] = String(value);
      } else if (graph.meta.inferredFields.includes(path)) {
        inferred[path] = String(value);
      }
    }
  };

  checkNode(graph.character as unknown as Record<string, unknown>, 'character');
  checkNode(graph.camera as unknown as Record<string, unknown>, 'camera');
  checkNode(graph.lighting as unknown as Record<string, unknown>, 'lighting');
  checkNode(graph.environment as unknown as Record<string, unknown>, 'environment');
  checkNode(graph.style as unknown as Record<string, unknown>, 'style');

  return { userSpecified, inferred, nullFields };
}
